import type { Dispatch, ReactNode, SetStateAction } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { nanoid } from 'nanoid'
import { useLocation, useMatches } from 'react-router-dom'

import { paths } from '@/paths'
import { userHasAssistantAccess } from '@/lib/assistant-access'
import {
	createAssistantThread,
	deleteAssistantThread,
	enrichAssistantPageContext,
	fetchAssistantContextLabels,
	getAssistantMessages,
	listAssistantThreads,
	streamAssistantMessage,
	submitAssistantFeedback,
	type AssistantCitation,
	type AssistantDataSource,
	type AssistantInputMethod,
	type AssistantStreamEvent,
	type AssistantThread,
} from '@/lib/assistant-api'
import { canRetryAssistantMessage } from '@/lib/assistant-message-utils'
import { normalizeAssistantNavigatePath } from '@/lib/assistant-navigate'
import { getOperatorScopeKey } from '@/lib/operator'
import {
	buildAssistantPageContext,
	enrichPageContextWithLabels,
	getPageNameFromMatches,
	type AssistantPageContext,
} from '@/lib/assistant-page-context'
import { stopAssistantSpeech } from '@/lib/assistant-speech'
import { extractDataSourceFromToolCalls } from '@/lib/assistant-tool-metadata'
import { track } from '@/lib/segment'
import { useUser } from '@/hooks/use-user'
import { useWorkspaces } from '@/hooks/use-workspaces'
import {
	parsePromotionDraftFromToolResult,
	type PromotionDraftAction,
} from '@/components/dashboard/assistant/promotion-form-widget'

const THREAD_STORAGE_KEY = 'platform-assistant-thread-id'
const PANEL_OPEN_STORAGE_KEY = 'platform-assistant-panel-open'

interface StoredAssistantThread {
	operator_key: string | null
	thread_id: string
}

// Threads are scoped by the x-operator-id header, so the persisted thread is
// tagged with the operator it was created under and only restored for that
// operator — a stale id would 404 under a different operator's scope.
function readStoredThreadId(): string | null {
	if (typeof window === 'undefined') return null
	try {
		const stored = JSON.parse(localStorage.getItem(THREAD_STORAGE_KEY) ?? '') as Partial<StoredAssistantThread>
		if (typeof stored?.thread_id !== 'string' || stored.operator_key !== getOperatorScopeKey()) return null
		return stored.thread_id
	} catch {
		return null
	}
}

function storeThreadId(threadId: string) {
	try {
		const stored: StoredAssistantThread = { operator_key: getOperatorScopeKey(), thread_id: threadId }
		localStorage.setItem(THREAD_STORAGE_KEY, JSON.stringify(stored))
	} catch {
		// localStorage unavailable
	}
}

function readInitialPanelOpen(): boolean {
	if (typeof window === 'undefined') return false
	return localStorage.getItem(PANEL_OPEN_STORAGE_KEY) === 'true'
}

function persistPanelOpen(isOpen: boolean) {
	try {
		if (isOpen) localStorage.setItem(PANEL_OPEN_STORAGE_KEY, 'true')
		else localStorage.removeItem(PANEL_OPEN_STORAGE_KEY)
	} catch {
		// localStorage unavailable
	}
}

export interface ToolCallState {
	id: string
	name: string
	status: 'running' | 'done' | 'error'
	result?: string
	args?: Record<string, unknown>
}

export interface NavigateAction {
	label: string
	path: string
}

export interface PlanogramDraftChange {
	slot_label: string | null
	cell_index: number
	from_product_name: string | null
	to_product_name: string | null
	from_side_photo?: string | null
	to_side_photo?: string | null
	/** @deprecated Prefer from_product_name */
	from_recipe_name?: string | null
	/** @deprecated Prefer to_product_name */
	to_recipe_name?: string | null
}

export interface PlanogramDraftAction {
	label: string
	path: string
	planogramLayoutId: number
	storeId: number
	siteId: string
	appliedChanges?: PlanogramDraftChange[]
}

export interface ChatMessage {
	id: string
	/** Server-side message id, assigned when the stream completes. `id` stays
	 * stable so the message's React key never changes (a changing key remounts
	 * the bubble and replays every widget entrance animation). */
	serverId?: string
	role: 'user' | 'assistant'
	content: string
	inputMethod?: AssistantInputMethod
	pageContext?: AssistantPageContext
	toolCalls?: ToolCallState[]
	citations?: AssistantCitation[]
	dataSource?: AssistantDataSource
	navigateActions?: NavigateAction[]
	planogramDraftActions?: PlanogramDraftAction[]
	promotionDraftActions?: PromotionDraftAction[]
	isStreaming?: boolean
	feedbackRating?: 1 | -1 | null
}

interface ChatContextValue {
	isOpen: boolean
	openPanel: () => void
	closePanel: () => void
	togglePanel: () => void
	messages: ChatMessage[]
	isLoading: boolean
	isLoadingHistory: boolean
	error: string | null
	threadId: string | null
	threads: AssistantThread[]
	hasAccess: boolean
	refreshThreads: () => Promise<void>
	beginDraftConversation: () => void
	cancelDraftConversation: () => void
	isDraftConversation: boolean
	includePageContext: boolean
	togglePageContext: () => void
	sendMessage: (content: string, options?: { inputMethod?: AssistantInputMethod }) => Promise<void>
	resendMessage: (assistantMessageId: string) => Promise<void>
	stopStreaming: () => void
	startNewChat: (content: string, options?: { inputMethod?: AssistantInputMethod }) => Promise<void>
	clearThread: () => Promise<void>
	selectThread: (threadId: string) => Promise<void>
	deleteThread: (threadId: string) => Promise<void>
	submitFeedback: (messageId: string, rating: 1 | -1, comment?: string) => Promise<void>
}

const ChatContext = createContext<ChatContextValue | null>(null)

function mapPersistedMessage(message: Awaited<ReturnType<typeof getAssistantMessages>>[number]): ChatMessage {
	const toolCalls = message.tool_calls ?? undefined
	return {
		id: message.id,
		role: message.role,
		content: message.content,
		inputMethod: message.input_method ?? undefined,
		pageContext: message.page_context ?? undefined,
		toolCalls,
		dataSource: toolCalls?.length ? extractDataSourceFromToolCalls(toolCalls) : undefined,
		feedbackRating: message.feedback_rating === 1 || message.feedback_rating === -1 ? message.feedback_rating : null,
	}
}

async function resolvePageContextForSend(
	pathname: string,
	pageName: string | undefined,
	search?: string
): Promise<AssistantPageContext> {
	const base = buildAssistantPageContext(pathname, pageName, search)
	if (!base.storeId && !base.productId && !base.siteId && !base.cabinetId) {
		return base
	}

	try {
		const enriched = await enrichAssistantPageContext(base)
		return enriched as AssistantPageContext
	} catch {
		try {
			const labels = await fetchAssistantContextLabels(base)
			return enrichPageContextWithLabels(base, labels)
		} catch {
			return base
		}
	}
}

function parsePlanogramDraftFromToolResult(result: string): PlanogramDraftAction | null {
	try {
		const parsed = JSON.parse(result)
		if (!parsed?.__apply_planogram_draft) return null
		const storeId = Number(parsed.store_id)
		const siteId = String(parsed.site_id ?? '')
		const planogramLayoutId = Number(parsed.planogram_layout_id)
		const appliedChanges = Array.isArray(parsed.applied_changes)
			? parsed.applied_changes.map((change: Record<string, unknown>) => {
					const fromProduct = change.from_product_name != null ? String(change.from_product_name) : null
					const toProduct = change.to_product_name != null ? String(change.to_product_name) : null
					const fromRecipe = change.from_recipe_name != null ? String(change.from_recipe_name) : null
					const toRecipe = change.to_recipe_name != null ? String(change.to_recipe_name) : null
					return {
						slot_label: change.slot_label != null ? String(change.slot_label) : null,
						cell_index: Number(change.cell_index),
						from_product_name: fromProduct ?? fromRecipe,
						to_product_name: toProduct ?? toRecipe,
						from_side_photo: change.from_side_photo != null ? String(change.from_side_photo) : null,
						to_side_photo: change.to_side_photo != null ? String(change.to_side_photo) : null,
						from_recipe_name: fromRecipe ?? fromProduct,
						to_recipe_name: toRecipe ?? toProduct,
					}
				})
			: undefined
		return {
			label: parsed.label ?? 'Review draft planogram',
			path:
				Number.isFinite(storeId) && siteId && Number.isFinite(planogramLayoutId)
					? paths.dashboard.stores.cabinets.planogram.edit(storeId, siteId, planogramLayoutId)
					: normalizeAssistantNavigatePath(String(parsed.path ?? '').replace(/\/new\/?$/, '')),
			planogramLayoutId,
			storeId,
			siteId,
			appliedChanges,
		}
	} catch {
		return null
	}
}

async function processAssistantStreamEvents(
	events: AsyncIterable<AssistantStreamEvent>,
	assistantId: string,
	setMessages: Dispatch<SetStateAction<ChatMessage[]>>,
	setError: Dispatch<SetStateAction<string | null>>
): Promise<boolean> {
	let completed = false
	for await (const event of events) {
		if (event.type === 'message_delta') {
			setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, content: m.content + event.content } : m)))
		} else if (event.type === 'tool_start') {
			setMessages((prev) =>
				prev.map((m) =>
					m.id === assistantId
						? {
								...m,
								toolCalls: [...(m.toolCalls ?? []), { id: event.id, name: event.name, status: 'running' }],
							}
						: m
				)
			)
		} else if (event.type === 'tool_end') {
			let navAction: NavigateAction | null = null
			let planogramDraftAction: PlanogramDraftAction | null = null
			let promotionDraftAction: PromotionDraftAction | null = null
			if (event.result) {
				try {
					const parsed = JSON.parse(event.result)
					if (parsed?.__navigate) {
						navAction = {
							label: parsed.label,
							path: normalizeAssistantNavigatePath(String(parsed.path ?? '')),
						}
					}
				} catch {
					/* not JSON or not navigate */
				}
				planogramDraftAction = event.result ? parsePlanogramDraftFromToolResult(event.result) : null
				promotionDraftAction = event.result ? parsePromotionDraftFromToolResult(event.result) : null
			}
			setMessages((prev) =>
				prev.map((m) =>
					m.id === assistantId
						? {
								...m,
								toolCalls: (m.toolCalls ?? []).map((tc) =>
									tc.id === event.id
										? {
												...tc,
												status: event.isError ? 'error' : 'done',
												result: event.result,
											}
										: tc
								),
								...(navAction ? { navigateActions: [...(m.navigateActions ?? []), navAction] } : {}),
								...(planogramDraftAction
									? {
											planogramDraftActions: [...(m.planogramDraftActions ?? []), planogramDraftAction],
										}
									: {}),
								...(promotionDraftAction
									? {
											promotionDraftActions: [...(m.promotionDraftActions ?? []), promotionDraftAction],
										}
									: {}),
							}
						: m
				)
			)
		} else if (event.type === 'message_done') {
			completed = true
			setMessages((prev) =>
				prev.map((m) =>
					m.id === assistantId
						? {
								...m,
								serverId: event.messageId ?? m.serverId,
								content: event.content || m.content,
								citations: event.citations,
								dataSource: event.dataSource,
								isStreaming: false,
							}
						: m
				)
			)
		} else if (event.type === 'error') {
			completed = false
			setError(event.message)
			setMessages((prev) =>
				prev.map((m) =>
					m.id === assistantId
						? {
								...m,
								serverId: event.messageId ?? m.serverId,
								content: `Error: ${event.message}`,
								isStreaming: false,
							}
						: m
				)
			)
		}
	}

	setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, isStreaming: false } : m)))
	return completed
}

export function ChatProvider({ children }: { children: ReactNode }) {
	const { user } = useUser()
	const hasAccess = userHasAssistantAccess(user)
	const { currentOperator } = useWorkspaces()
	const location = useLocation()
	const matches = useMatches()

	const [isOpen, setIsOpenState] = useState(readInitialPanelOpen)

	const setPanelOpen = useCallback((next: boolean | ((prev: boolean) => boolean)) => {
		setIsOpenState((prev) => {
			const value = typeof next === 'function' ? next(prev) : next
			persistPanelOpen(value)
			return value
		})
	}, [])
	const [messages, setMessages] = useState<ChatMessage[]>([])
	const [isLoading, setIsLoading] = useState(false)
	const [isLoadingHistory, setIsLoadingHistory] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [threadId, setThreadId] = useState<string | null>(readStoredThreadId)
	const [threads, setThreads] = useState<AssistantThread[]>([])
	const draftSnapshotRef = useRef<{ threadId: string | null; messages: ChatMessage[] } | null>(null)
	const streamAbortRef = useRef<AbortController | null>(null)
	// Monotonic counter for thread loads — a stale load must not resume a
	// reply for a thread the user has already navigated away from.
	const threadLoadSeqRef = useRef(0)
	const [isDraftConversation, setIsDraftConversation] = useState(false)
	// Composer toggle — when off, the next question sends without page context.
	const [includePageContext, setIncludePageContext] = useState(true)
	const togglePageContext = useCallback(() => {
		setIncludePageContext((v) => !v)
	}, [])

	// Claims the shared abort slot for a new request (aborting any previous
	// one) before async preparation, so Stop also cancels sends still
	// resolving page context or creating a thread.
	const beginAssistantRequest = useCallback(() => {
		streamAbortRef.current?.abort()
		const controller = new AbortController()
		streamAbortRef.current = controller
		return controller
	}, [])

	// Abort the in-flight SSE stream and settle the partially-streamed
	// message — content received so far is kept.
	const stopStreaming = useCallback(() => {
		streamAbortRef.current?.abort()
		streamAbortRef.current = null
		setIsLoading(false)
		setMessages((prev) => prev.map((m) => (m.isStreaming ? { ...m, isStreaming: false } : m)))
	}, [])

	// Runs an assistant stream under the shared abort controller and settles
	// the placeholder message: partial content is kept on AbortError, other
	// failures write "Error: …" into the bubble (unless writeErrorToMessage is
	// off) and surface in the panel banner. Resolves true only when the
	// server confirmed the reply (message_done), so callers can gate
	// post-success work. Loading state is only cleared by the request that
	// still owns the abort slot, so a stopped stream can't clobber a newer one.
	const runAssistantStream = useCallback(
		async (
			assistantId: string,
			createStream: (
				signal: AbortSignal
			) => AsyncIterable<AssistantStreamEvent> | Promise<AsyncIterable<AssistantStreamEvent>>,
			options?: { fallbackError?: string; writeErrorToMessage?: boolean; controller?: AbortController }
		): Promise<boolean> => {
			const controller = options?.controller ?? beginAssistantRequest()
			try {
				controller.signal.throwIfAborted()
				return await processAssistantStreamEvents(
					await createStream(controller.signal),
					assistantId,
					setMessages,
					setError
				)
			} catch (err: any) {
				if (err?.name === 'AbortError') {
					setMessages((prev) => prev.map((m) => (m.id === assistantId ? { ...m, isStreaming: false } : m)))
					return false
				}
				const message = err?.message ?? options?.fallbackError ?? 'Something went wrong'
				setError(message)
				const writeError = options?.writeErrorToMessage !== false
				setMessages((prev) =>
					prev.map((m) =>
						m.id === assistantId
							? { ...m, ...(writeError ? { content: `Error: ${message}` } : {}), isStreaming: false }
							: m
					)
				)
				return false
			} finally {
				if (streamAbortRef.current === controller) {
					streamAbortRef.current = null
					setIsLoading(false)
				}
			}
		},
		[beginAssistantRequest]
	)

	const refreshThreads = useCallback(async () => {
		if (!hasAccess) return
		try {
			const list = await listAssistantThreads()
			setThreads(list)
		} catch {
			// non-fatal
		}
	}, [hasAccess])

	// Regenerates the reply for a thread whose last exchange is an unanswered
	// user message — the shape left behind when a send is abandoned mid-stream
	// (thread switch, page reload, dropped connection) before any output was
	// persisted. Streams it live so reopening the thread shows a response
	// instead of a silent gap.
	const resumeThreadReply = useCallback(
		async (id: string) => {
			setIsLoading(true)
			setError(null)
			const assistantId = nanoid()
			setMessages((prev) => [
				...prev,
				{ id: assistantId, role: 'assistant', content: '', toolCalls: [], isStreaming: true },
			])
			const ok = await runAssistantStream(assistantId, (signal) =>
				streamAssistantMessage(id, '', [], undefined, { retry: true, signal })
			)
			if (ok) {
				void refreshThreads()
				window.setTimeout(() => {
					void refreshThreads()
				}, 2500)
			}
		},
		[refreshThreads, runAssistantStream]
	)

	const loadThreadMessages = useCallback(
		async (id: string) => {
			const seq = ++threadLoadSeqRef.current
			setIsLoadingHistory(true)
			setError(null)
			const operatorAtLoad = getOperatorScopeKey()
			try {
				const persisted = await getAssistantMessages(id)
				// Operator switched mid-load — this thread belongs to the previous scope.
				if (getOperatorScopeKey() !== operatorAtLoad) return
				const mapped = persisted.map(mapPersistedMessage)
				setMessages(mapped)
				setThreadId(id)
				storeThreadId(id)
				// Only the latest load may resume — a superseded one would stream
				// its answer into whatever thread is open now.
				if (seq === threadLoadSeqRef.current && mapped[mapped.length - 1]?.role === 'user') {
					void resumeThreadReply(id)
				}
			} catch (err: any) {
				// A stale request's failure must not clobber the new operator's state.
				if (getOperatorScopeKey() === operatorAtLoad) {
					setError(err?.message ?? 'Failed to load conversation')
					setMessages([])
				}
			} finally {
				if (getOperatorScopeKey() === operatorAtLoad) setIsLoadingHistory(false)
			}
		},
		[resumeThreadReply]
	)

	useEffect(() => {
		if (!hasAccess) return
		void refreshThreads()
	}, [hasAccess, refreshThreads])

	useEffect(() => {
		if (!hasAccess || !threadId) return
		void loadThreadMessages(threadId)
		// eslint-disable-next-line react-hooks/exhaustive-deps -- load once when thread id restored
	}, [hasAccess])

	const currentOperatorId = currentOperator?.id
	// Requests are scoped by the x-operator-id cookie, not currentOperator
	// state — track the cookie itself. Its value is written before operator
	// state settles, and stays constant through the initial cookie-restore,
	// so only a real switch produces a scope change (load transitions that
	// resolve currentOperator to the already-stored operator never look like
	// a switch and can't clobber the restored thread).
	const previousScopeRef = useRef<string | null>(getOperatorScopeKey())

	// Switching operators changes the x-operator-id scope of every assistant
	// request, so all thread state must be dropped — otherwise the panel keeps
	// querying the previous operator's thread (404s on load and send).
	useEffect(() => {
		const scope = getOperatorScopeKey()
		if (previousScopeRef.current === scope) return
		const hadScope = previousScopeRef.current !== null
		previousScopeRef.current = scope
		if (!hadScope) return

		streamAbortRef.current?.abort()
		streamAbortRef.current = null
		stopAssistantSpeech()
		setIsLoading(false)
		setIsLoadingHistory(false)
		setMessages([])
		setThreadId(null)
		setThreads([])
		setError(null)
		draftSnapshotRef.current = null
		setIsDraftConversation(false)
		void refreshThreads()
	}, [currentOperatorId, refreshThreads])

	const ensureThread = useCallback(
		async (title: string, signal?: AbortSignal) => {
			if (threadId) return threadId
			const operatorAtCreate = getOperatorScopeKey()
			const thread = await createAssistantThread(title)
			// The request was abandoned mid-create (Stop pressed, conversation
			// switched) — don't adopt a thread the caller already left behind.
			signal?.throwIfAborted()
			// Operator switched mid-create — the thread belongs to the previous
			// scope; abort rather than adopt it.
			if (getOperatorScopeKey() !== operatorAtCreate) throw new DOMException('Aborted', 'AbortError')
			setThreadId(thread.id)
			storeThreadId(thread.id)
			setThreads((prev) => [thread, ...prev.filter((t) => t.id !== thread.id)])
			return thread.id
		},
		[threadId]
	)

	const beginDraftConversation = useCallback(() => {
		stopAssistantSpeech()
		// Abandon the in-flight stream — it belongs to the conversation being
		// left. Letting it run would pin isLoading and leave the new draft's
		// suggested prompts dead until the hidden stream finishes.
		stopStreaming()
		// Snapshot with isStreaming settled so a restored conversation never
		// replays a phantom "generating" state on the aborted bubble.
		draftSnapshotRef.current = {
			threadId,
			messages: messages.map((m) => (m.isStreaming ? { ...m, isStreaming: false } : m)),
		}
		setMessages([])
		setThreadId(null)
		setError(null)
		localStorage.removeItem(THREAD_STORAGE_KEY)
		setIsDraftConversation(true)
	}, [messages, stopStreaming, threadId])

	const cancelDraftConversation = useCallback(() => {
		stopAssistantSpeech()
		// Abandon any send still streaming inside the draft.
		stopStreaming()
		const snapshot = draftSnapshotRef.current
		if (snapshot) {
			setMessages(snapshot.messages)
			setThreadId(snapshot.threadId)
			if (snapshot.threadId) {
				storeThreadId(snapshot.threadId)
			}
		}
		draftSnapshotRef.current = null
		setIsDraftConversation(false)
	}, [stopStreaming])

	const sendMessage = useCallback(
		async (content: string, options?: { inputMethod?: AssistantInputMethod }) => {
			const trimmed = content.trim()
			if (!trimmed || isLoading || !hasAccess) return
			const inputMethod: AssistantInputMethod = options?.inputMethod ?? 'text'

			setPanelOpen(true)
			setIsLoading(true)
			setError(null)

			void track('AssistantQuestionSent', {
				threadId,
				messageLength: trimmed.length,
				inputMethod,
				path: location.pathname,
				includePageContext,
			})

			const controller = beginAssistantRequest()
			const pageContext = includePageContext
				? await resolvePageContextForSend(location.pathname, getPageNameFromMatches(matches), location.search)
				: undefined
			if (controller.signal.aborted) return

			const userMessage: ChatMessage = {
				id: nanoid(),
				role: 'user',
				content: trimmed,
				inputMethod,
				pageContext,
			}
			const assistantId = nanoid()
			setMessages((prev) => [
				...prev,
				userMessage,
				{ id: assistantId, role: 'assistant', content: '', toolCalls: [], isStreaming: true },
			])

			const ok = await runAssistantStream(
				assistantId,
				async (signal) => {
					const activeThreadId = await ensureThread(trimmed, signal)
					signal.throwIfAborted()
					return streamAssistantMessage(activeThreadId, trimmed, [], pageContext, { signal, inputMethod })
				},
				{ controller }
			)
			if (ok) {
				void refreshThreads()
				window.setTimeout(() => {
					void refreshThreads()
				}, 2500)
				if (isDraftConversation) {
					draftSnapshotRef.current = null
					setIsDraftConversation(false)
				}
			}
		},
		[
			beginAssistantRequest,
			ensureThread,
			hasAccess,
			includePageContext,
			isDraftConversation,
			isLoading,
			location.pathname,
			location.search,
			matches,
			refreshThreads,
			runAssistantStream,
			setPanelOpen,
			threadId,
		]
	)

	const resendMessage = useCallback(
		async (assistantMessageId: string) => {
			if (isLoading || !hasAccess || !threadId) return

			const failedIndex = messages.findIndex((m) => m.id === assistantMessageId)
			if (failedIndex === -1) return
			const failedMessage = messages[failedIndex]
			if (!canRetryAssistantMessage(failedMessage)) return

			const hasUserMessage = messages.slice(0, failedIndex).some((m) => m.role === 'user')
			if (!hasUserMessage) return

			setPanelOpen(true)
			setIsLoading(true)
			setError(null)

			void track('AssistantMessageRetried', { threadId, assistantMessageId })

			const newAssistantId = nanoid()
			setMessages((prev) => [
				...prev.filter((m) => m.id !== assistantMessageId),
				{ id: newAssistantId, role: 'assistant', content: '', toolCalls: [], isStreaming: true },
			])

			const ok = await runAssistantStream(newAssistantId, (signal) =>
				streamAssistantMessage(threadId, '', [], undefined, { retry: true, signal })
			)
			if (ok) {
				void refreshThreads()
				window.setTimeout(() => {
					void refreshThreads()
				}, 2500)
			}
		},
		[hasAccess, isLoading, messages, refreshThreads, runAssistantStream, setPanelOpen, threadId]
	)

	const clearThread = useCallback(async () => {
		stopAssistantSpeech()
		stopStreaming()
		setMessages([])
		setThreadId(null)
		setError(null)
		localStorage.removeItem(THREAD_STORAGE_KEY)
		try {
			const thread = await createAssistantThread('New conversation')
			setThreadId(thread.id)
			storeThreadId(thread.id)
			void refreshThreads()
		} catch {
			// user can still send — thread created on first message
		}
	}, [refreshThreads, stopStreaming])

	const startNewChat = useCallback(
		async (content: string, options?: { inputMethod?: AssistantInputMethod }) => {
			const trimmed = content.trim()
			if (!trimmed || !hasAccess) return
			const inputMethod: AssistantInputMethod = options?.inputMethod ?? 'text'

			setMessages([])
			setThreadId(null)
			setError(null)
			localStorage.removeItem(THREAD_STORAGE_KEY)
			setPanelOpen(true)
			setIsLoading(true)

			void track('AssistantQuestionSent', {
				threadId: null,
				messageLength: trimmed.length,
				source: 'quickfind',
				inputMethod,
				path: location.pathname,
				includePageContext,
			})

			const controller = beginAssistantRequest()
			const pageContext = includePageContext
				? await resolvePageContextForSend(location.pathname, getPageNameFromMatches(matches), location.search)
				: undefined
			if (controller.signal.aborted) return

			const userMessage: ChatMessage = {
				id: nanoid(),
				role: 'user',
				content: trimmed,
				inputMethod,
				pageContext,
			}
			const assistantId = nanoid()
			setMessages([userMessage, { id: assistantId, role: 'assistant', content: '', toolCalls: [], isStreaming: true }])

			await runAssistantStream(
				assistantId,
				async (signal) => {
					const thread = await createAssistantThread(trimmed)
					signal.throwIfAborted()
					setThreadId(thread.id)
					storeThreadId(thread.id)
					setThreads((prev) => [thread, ...prev.filter((t) => t.id !== thread.id)])
					return streamAssistantMessage(thread.id, trimmed, [], pageContext, { signal, inputMethod })
				},
				{ fallbackError: 'Failed to send message', writeErrorToMessage: false, controller }
			)
			void refreshThreads()
			window.setTimeout(() => {
				void refreshThreads()
			}, 2500)
		},
		[
			beginAssistantRequest,
			hasAccess,
			includePageContext,
			location.pathname,
			location.search,
			matches,
			refreshThreads,
			runAssistantStream,
			setPanelOpen,
		]
	)

	const selectThread = useCallback(
		async (id: string) => {
			if (id === threadId) return
			stopAssistantSpeech()
			// Abandon the in-flight stream — it belongs to the thread being
			// left; otherwise isLoading stays pinned in the newly opened one.
			stopStreaming()
			await loadThreadMessages(id)
		},
		[loadThreadMessages, stopStreaming, threadId]
	)

	const deleteThread = useCallback(
		async (id: string) => {
			await deleteAssistantThread(id)
			setThreads((prev) => prev.filter((thread) => thread.id !== id))
			if (threadId === id) {
				stopAssistantSpeech()
				stopStreaming()
				setMessages([])
				setThreadId(null)
				setError(null)
				localStorage.removeItem(THREAD_STORAGE_KEY)
			}
			void refreshThreads()
		},
		[refreshThreads, stopStreaming, threadId]
	)

	const submitFeedback = useCallback(async (messageId: string, rating: 1 | -1, comment?: string) => {
		const matchesMessage = (m: ChatMessage) => (m.serverId ?? m.id) === messageId
		setMessages((prev) => prev.map((m) => (matchesMessage(m) ? { ...m, feedbackRating: rating } : m)))
		void track('AssistantFeedback', { messageId, rating, comment })
		try {
			await submitAssistantFeedback(messageId, rating, comment)
		} catch {
			setMessages((prev) => prev.map((m) => (matchesMessage(m) ? { ...m, feedbackRating: null } : m)))
		}
	}, [])

	useEffect(() => {
		if (!hasAccess) return
		const handler = (event: KeyboardEvent) => {
			if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'j') {
				event.preventDefault()
				setPanelOpen((v) => !v)
			}
		}
		document.addEventListener('keydown', handler)
		return () => {
			document.removeEventListener('keydown', handler)
		}
	}, [hasAccess, setPanelOpen])

	const value = useMemo<ChatContextValue>(
		() => ({
			isOpen: hasAccess && isOpen,
			openPanel: () => {
				setPanelOpen(true)
			},
			closePanel: () => {
				setPanelOpen(false)
			},
			togglePanel: () => {
				setPanelOpen((v) => !v)
			},
			messages,
			isLoading,
			isLoadingHistory,
			error,
			threadId,
			threads,
			hasAccess,
			refreshThreads,
			beginDraftConversation,
			cancelDraftConversation,
			isDraftConversation,
			includePageContext,
			togglePageContext,
			sendMessage,
			resendMessage,
			stopStreaming,
			startNewChat,
			clearThread,
			selectThread,
			deleteThread,
			submitFeedback,
		}),
		[
			isOpen,
			setPanelOpen,
			messages,
			isLoading,
			isLoadingHistory,
			error,
			threadId,
			threads,
			hasAccess,
			refreshThreads,
			beginDraftConversation,
			cancelDraftConversation,
			isDraftConversation,
			includePageContext,
			togglePageContext,
			sendMessage,
			resendMessage,
			stopStreaming,
			startNewChat,
			clearThread,
			selectThread,
			deleteThread,
			submitFeedback,
		]
	)

	return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
}

export function useChat(): ChatContextValue {
	const ctx = useContext(ChatContext)
	if (!ctx) throw new Error('useChat must be used within ChatProvider')
	return ctx
}
